
import { computed, defineComponent, onBeforeMount, PropType, reactive, ref } from 'vue'

// @components
import NewModal from '@/common/new-components/NewModal/index.vue'
import NewSelect from '@/ui-kit/NewSelect/index.vue'
import NewCheckbox from '@/common/new-components/NewCheckbox/index.vue'
import NewInput from '@/ui-kit/NewInput/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'
import RadioGroup from '@/common/components/UI/RadioGroup/index.vue'

import exportIcon from '@/assets/icons/export.svg'
import uploadIcon from '@/assets/icons/upload.svg'
import excelIcon from '@/assets/icons/excel.svg'
import timesGreyIcon from '@/assets/icons/timesGrey.svg'

// @types
import { CheckImportFileItem } from '@/new-services/types/tms'

// @services
import tmsAPI from '@/new-services/tms.service'

// @validations
import useNotifications from '@/common/composable/useNotifications'
import { useFormData } from '@/common/composable/useFormData'

export default defineComponent({
    components: {
        NewModal,
        NewCheckbox,
        NewSelect,
        NewInput,
        Loader,
        exportIcon,
        uploadIcon,
        excelIcon,
        timesGreyIcon,
        RadioGroup,
    },
    props: {
        loading: {
            type: Boolean,
            default: false,
        },
        direction: {
            type: String,
            default: '... - ...',
        },
        parentDirectionId: {
            type: Number,
            default: null,
        },
    },
    emits: ['submit', 'cancel'],
    setup(props, { emit }) {
        const notifications = useNotifications()

        const state = reactive({
            is_loading: false,
            excel_response: [] as CheckImportFileItem[],
        })

        const file = ref(null)
        const isDragging = ref(false)

        const { getFormData } = useFormData()

        const onSubmitExcel = async () => {
            if (!props.parentDirectionId) return

            let excelFiltered: { directionId: number }[] = state.excel_response.filter(
                (item) => item.isSuccess
            )
            excelFiltered = excelFiltered.map((item: { directionId: number }) => {
                return {
                    directionId: item.directionId,
                }
            })

            if (!excelFiltered.length) {
                notifications.notify({
                    title: 'При обработке файла импорта обнаружены направления, которые невозможно импортировать.',
                    description:
                        'Пожалуйста, внесите изменения в файл / систему и повторите попытку.',
                    type: 'danger',
                })
                return
            }

            try {
                state.is_loading = true

                const params = {
                    parentDirectionId: props.parentDirectionId,
                    internalDirections: excelFiltered,
                }

                await tmsAPI.importInternalDirections(params)
                notifications.notify({
                    title: 'Внутренние направления были успешно прикреплены!',
                    type: 'success',
                })

                emit('submit')
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: `Ошибка при загрузке файла`,
                })
            } finally {
                state.is_loading = false
            }
        }

        const importExcel = async () => {
            if (!file.value) return

            try {
                state.is_loading = true

                const response = await tmsAPI.checkImportFile(getFormData({ file: file.value }))
                state.excel_response = response.data.data
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: `Ошибка при загрузке файла`,
                })
            } finally {
                state.is_loading = false
            }
        }

        const emitCancel = () => {
            if (props.loading) return
            emit('cancel')
        }

        onBeforeMount(() => {})

        const handleFile = (e: any) => {
            const selected = e.target.files[0]
            if (selected) file.value = selected
        }

        const handleDrop = (e: any) => {
            isDragging.value = false
            const dropped = e.dataTransfer.files[0]
            if (dropped) file.value = dropped
        }

        const removeFile = () => {
            file.value = null
        }

        const formatSize = (size: number) => {
            const kb = size / 1024
            if (kb < 1024) return `${kb.toFixed(1)} KB`
            return `${(kb / 1024).toFixed(1)} MB`
        }

        const cancelExcel = () => {
            state.excel_response = []
        }

        //

        return {
            state,
            emitCancel,
            importExcel,
            file,
            isDragging,
            handleFile,
            handleDrop,
            removeFile,
            formatSize,
            cancelExcel,
            onSubmitExcel,
        }
    },
})
